/**
 * # CurrencyInput
 * A currency input container.
 *
 * @example
 * new Spark.Payments.CurrencyInput(el, {
 *   // Optional. Callback for when the input value changes.
 *   onChange(value, inputInstance) {}
 * });
 *
 * @module components/payments/currency-input.js
 */
import BaseComponent from '../base';
import toggleClass from '../../helpers/dom/toggle-class';
import mixin from '../../helpers/util/mixin';
import messaging from '../../mixins/messaging';
import validation from '../../mixins/validation';
import makeElement from '../../helpers/dom/make-element';
import isInteger from '../../helpers/util/is-integer';

const noop = function() {};

class CurrencyInput extends BaseComponent {

  /**
   * CurrencyInput constructor.
   * @param {Element} el
   * @param {Object} params
   */
  constructor(el, params = {}) {
    params.elRequired = true;

    if (!super(el, params)) {
      return;
    }

    this._bindEventListenerCallbacks();
    this._addEventListeners();

    if (this.inputEl.value) {
      this._formatValue();
      this.show();
      this._onInput();
    }
  }


  /**
   * Show the input by adding the active state.
   */
  show() {
    this.isActive = true;
    this._updateClass();
    return this;
  }


  /**
   * Hide the input by removing the active state.
   */
  hide() {
    this.isActive = false;
    this._updateClass();
    return this;
  }


  /**
   * Set the value of the input.
   * @param {Mixed} value
   */
  setValue(value) {
    this.inputEl.value = value;

    this._formatValue();

    if (value) {
      this.show();
    } else {
      this.hide();
    }

    (this.onChange || noop)(this.inputEl.value, this);

    return this;
  }


  /**
   * Get the value of the input.
   * @return {String}
   */
  getValue() {
    return this.inputEl.value;
  }


  /**
   * Clear the value of the input.
   */
  clearValue() {
    this.inputEl.value = '';
    return this;
  }


  /**
   * Disable the input.
   */
  disable() {
    toggleClass(this.el, 'disabled', true);
    this.inputEl.setAttribute('disabled', '');
    return this;
  }


  /**
   * Enable the input.
   */
  enable() {
    toggleClass(this.el, 'disabled', false);
    this.inputEl.removeAttribute('disabled');
    return this;
  }


  /**
   * Remove.
   * @param {Boolean} leaveElement
   */
  remove(leaveElement) {
    return super.remove(leaveElement);
  }


  /**
   * Update the element in use and the position.
   * @param {Element} el
   */
  update(el, params) {
    return super.update(el, params);
  }

  /**
   * Store a reference to the needed elements.
   * @param {Element} el
   */
  _cacheElements(el) {
    this.el = el;
    this.inputEl = this.el.querySelector('input');

    if (!this.inputEl) {
      throw new Error('No <input> element present in input container!', this.el);
    }

    this.messageEl = this.el.querySelector('.spark-input__message') || makeElement('<span class="spark-input__message"></span>');
  }


  /**
   * Parse parameters from the elements.
   */
  _parseParams() {
    this.validatePattern = this.validatePattern || this.inputEl.getAttribute('data-validate');
    this.isActive = this.inputEl.value ? true : false;
  }


  /**
   * Create bound versions of event listener callbacks and store them.
   * Otherwise we can't unbind from these events later because the
   * function signatures won't match.
   */
  _bindEventListenerCallbacks() {
    this._onFocusBound = this._onFocus.bind(this);
    this._onBlurBound = this._onBlur.bind(this);
    this._onInputBound = this._onInput.bind(this);
    this._onKeydownBound = this._onKeydown.bind(this);
  }


  /**
   * Add event listeners for focus, blur, input, and click.
   */
  _addEventListeners() {
    this.inputEl.addEventListener('focus', this._onFocusBound);
    this.inputEl.addEventListener('blur', this._onBlurBound);
    this.inputEl.addEventListener('input', this._onInputBound);
    this.inputEl.addEventListener('keydown', this._onKeydownBound);
  }


  /**
   * Remove event listeners for focus, blur and input.
   */
  _removeEventListeners() {
    this.inputEl.removeEventListener('focus', this._onFocusBound);
    this.inputEl.removeEventListener('blur', this._onBlurBound);
    this.inputEl.removeEventListener('input', this._onInputBound);
    this.inputEl.removeventListener('keydown', this._onKeydownBound);
  }


  /**
   * Update the active class.
   */
  _updateClass() {
    toggleClass(this.el, 'active', this.isActive);
  }


  /**
   * Check for valid decimal separator
   * @param {Object} evt
   */
  _validDecimalSeparator(evt) {
    const val = this.inputEl.value;

    if (evt.key === this.decimalSeparator && val.indexOf(this.decimalSeparator) === -1) return true;

    return false;
  }


  /**
   * Check if the key pressed is valid for this type of input.
   * @param {Object} evt
   */
  _validKey(evt) {
    const key = evt.key;

    if (key === 'Enter') return true;
    if (key === 'Tab') return true;
    if (key === 'Backspace') return true;
    if (key === 'ArrowRight') return true;
    if (key === 'ArrowLeft') return true;
    if (key === 'Shift') return true;
    if (key === 'v' && evt.metaKey === true) return true;
    if (key === 'v' && evt.crtlKey === true) return true;
    if (key === 'c' && evt.metaKey === true) return true;
    if (key === 'c' && evt.crtlKey === true) return true;

    return false;
  }


  /**
   * Check precision of the value.
   */
  _validPrecision() {
    const val = this.inputEl.value;
    const currencyParts = val.split(this.decimalSeparator);

    if (currencyParts[1] && currencyParts[1].length === this.precision) return false;

    return true;
  }


  /**
   * Filter key events.
   * @param {Object} evt 
   * 
   */
  _onKeydown(evt) {
    const val = this.inputEl.value;
    const hasDecimalSeparator = (val.indexOf(this.decimalSeparator) !== -1);
    const validInteger = isInteger(Number(evt.key));

    // Check for special keys pass them
    if (this._validKey(evt) === true) return true;

    if (validInteger && hasDecimalSeparator && val.indexOf(this.decimalSeparator) >= evt.target.selectionStart) {
      return true;
    }

    if (validInteger && !this._validPrecision()) {
      evt.preventDefault();
    }

    // If the key is not a number and the it's not a valid decimal separator don't enter it.
    if (!validInteger  && !this._validDecimalSeparator(evt)) {
      evt.preventDefault();
    }
  }


  /** 
   * When the input element gains focus.
   */
  _onFocus() {
    this.show();
    toggleClass(this.el, 'focus', true);
    (this.onFocus || noop)(this.inputEl.value, this);
  }


  /** 
   * Format current value to conform to formatting rules.
   */
  _formatValue() {
    let val = this.inputEl.value;
    const regExLeadingSeparator = new RegExp("^\\" + this.decimalSeparator);

    if (regExLeadingSeparator.test(this.inputEl.value)) {
      val = "0" + val;
    }

    // Remove leading zeros ie 000001.00 would be 1.00 
    val = val.replace(/^0*([1-9]|0.)/, '$1');

    if (val.indexOf(this.decimalSeparator !== -1)) {
      val = val.replace(this.decimalSeparator, '.');
      val = !isNaN(Number.parseFloat(val, 10)) ? Number.parseFloat(val, 10) : 0.00;
      val = val.toFixed(this.precision);
      val = val.replace('.', this.decimalSeparator);
    }

    this.inputEl.value = this.inputEl.value === '' ? '' : val;
  }

  /**
   * When the input element loses focus.
   */
  _onBlur() {
    if (!this.inputEl.value) {
      this.hide();
    }

    this._formatValue();
    toggleClass(this.el, 'focus', false);
    (this.onBlur || noop)(this.inputEl.value, this);
  }


  /**
   * When the value is about to change, run the validation
   */
  _onInput() {
    this.validate();
    (this.onChange || noop)(this.inputEl.value, this);
  }
}


/**
 * Whitelisted parameters which can be set on construction.
 * @type {Array}
 */
 CurrencyInput.prototype._whitelistedParams = [
  'validate',
  'validatePattern',
  'onValidate',
  'onChange',
  'onFocus',
  'onBlur',
  'precision',
  'decimalSeparator'
];


/**
 * Default values for internal properties we will be setting.
 * These are set on each construction so we don't leak properties
 * into the prototype chain.
 * @type {Object}
 */
 CurrencyInput.prototype.defaults = {
  el: null,
  messageEl: null,
  inputEl: null,
  clearEl: null,
  isActive: false,
  precision: 2,
  decimalSeparator: '.',
  validatePattern: false,
  type: null,
  onValidate: noop,
  onChange: noop,
  onFocus: noop,
  onBlur: noop,
  _onFocusBound: null,
  _onBlurBound: null,
  _onInputBound: null,
};

mixin(CurrencyInput.prototype, messaging, validation);

export default CurrencyInput;
